import { Component, createRef } from 'react';
import NesGraphics from "../services/NesGraphics";
import GameData from "../../mobx/GameData";
import { observe, reaction } from 'mobx';

import "./NamEditor.scss";
import { ChrFile } from "./ChrFile";
import { PalettePreview } from "./PalettePreview";
import { Button, ToggleButton, ToggleButtonGroup, Container, Row, Col, DropdownButton, Dropdown, Modal } from "react-bootstrap";
import ApplicationState from "../../mobx/ApplicationState";
import { coerceUnknownFields } from '../../data/BaseGame';

import UndoManager from '../services/UndoManager';

export class NamEditor extends Component {

    gameDataCleanup = null;
    applicationStateCleanup = null;
    canvas = createRef();
    canvasContext = null;
    paletteGroup = createRef();
    tileCache = {}
    // NOTE: Keeping this outside state because we don't directly react to it, and 
    // this thing has horrendous performance. Every little bit helps.
    currentImageTileData = [];
    currentImageMetaData = [];
    combinedTileData = [];
    emulatorVisibleReaction = null;
    editorLocked = false;
    loadTilePipeline = [];
    drawTilePipeline = [];
    drawTilePipelineRunning = false;
    undoManager = new UndoManager();

    confirmData = {
        resolve: null,
        reject: null,
        promise: null
    }

    constructor(props) {
        super(props);

        this.state = {
            width: 32,
            height: 30,
            palettes: [],
            currentPalette: 0,
            currentTile: 0,
            isDragging: false,
            isTypingMode: false,
            is16pxMode: false,
            typingX: 0, 
            typingY: 0,
            isReadOnly: !!props.readOnly,

            awaitingConfirm: false,
            awaitingConfirmAction: 'Do it'
        }

        this.combinedTileData = [
            ...GameData?.currentGame?.gameGraphicsTiles,
            ...GameData?.currentGame?.gameGraphicsFont
        ];
    }

    async componentDidMount() {
        this.gameDataCleanup = observe(GameData, () => { 
            this.forceUpdate()
        });
        this.applicationStateCleanup = observe(ApplicationState, () => {
            this.forceUpdate()
        });

        this.emulatorVisibleReaction = reaction(
            () => ApplicationState.emulatorIsVisible,
            (isVisibleNow, wasVisibleBefore) => {
                if (isVisibleNow !== wasVisibleBefore && !isVisibleNow) {
                    setTimeout(() => {
                        this.initCanvas();
                        this.generateImage()
                    }, 5);
                }
            }  
        );
      
        if (GameData.currentGame) { 
            let tileData = null;
            if (this.props.fieldName) {
                tileData = coerceUnknownFields(GameData.currentGame)[this.props.fieldName];
            } else if (this.props.screenId) {
                const gd = coerceUnknownFields(GameData.currentGame);
                const screen = gd.staticScreens.find(a => a.id === this.props.screenId);
                tileData = screen.data;
            } else {
                throw new Error('Could not figure out what field to load for NamEditor');
            }
            this.currentImageTileData = tileData.slice(0, 32*30);
            this.currentImageMetaData = tileData.slice(32*30);
        }

        this.generateImage();

        if (!this.state.isReadOnly) {
            this.clearDragging = this.clearDragging.bind(this)
            document.addEventListener('mouseup', this.clearDragging);
            document.addEventListener('touchend', this.clearDragging);

            this.touchMove = this.touchMove.bind(this);
            document.addEventListener('touchmove', this.touchMove, {passive: false});

            this.doTyping = this.doTyping.bind(this);
            document.addEventListener('keydown', this.doTyping);
        }

        await this.initCanvas();

        // Give a little more time for things to init
        await new Promise(resolve => setTimeout(resolve, 500));
        this.smartPreloadTiles();
    }

    async setTile(x, y, val, palette = null) {
        if (palette) {
            await this.drawMetaTile(x, y, val, false, palette);
        } else {
            await this.drawMetaTile(x, y, val, false, this.getPaletteIdForTile(x, y));
        }
        // Not super ideal, but undo history will be toast after this
        return this.undoManager.reset();
    }

    componentWillUnmount() {
        if (this.gameDataCleanup) {
            this.gameDataCleanup();
        }
        if (this.applicationStateCleanup) {
            this.applicationStateCleanup();
        }

        if (this.emulatorVisibleReaction) {
            this.emulatorVisibleReaction();
        }
      
        document.removeEventListener('mouseup', this.clearDragging);
        document.removeEventListener('touchend', this.clearDragging);
        document.removeEventListener('touchmove', this.touchMove, {passive: false});
        document.removeEventListener('keydown', this.doTyping);
    }

    initCanvas() {
        this.canvasContext = this.canvas.current.getContext('2d');
        this.canvasContext.imageSmoothingEnabled = false;
    }

    clearDragging() {
        this.setState({isDragging: false});
    }

    componentDidUpdate() {
    }

    getPaletteForTile(x, y) {
        let id = this.state.palettes[this.getPaletteIdForTile(x, y)];
        if (typeof id === 'undefined' || id.length === 0) {
            console.warn('Somehow got undefined palette', x, y, id, this.getPaletteIdForTile(x, y));
            id = this.state.palettes[0];
        }
        return id;
    }

    getPaletteIdForTile(x, y) {
        const byteX = Math.floor(x/4), byteY = Math.floor(y/4);
        const theByte = this.currentImageMetaData[byteX + (byteY * 8)];
        const bitX = Math.floor(x / 2), bitY = Math.floor(y / 2);

        if ((bitX % 2) === 0) {
            if ((bitY % 2) === 0) {
                // Top left, 0shift
                return theByte & 0x03;
            } else {
                // Bottom left, 4shift
                return (theByte >> 4) & 0x03;
            }
        } else {
            if ((bitY % 2) === 0) {
                // top right, 2shift
                return (theByte >> 2) & 0x03;
            } else {
                // bottom right, 6shift
                return (theByte >> 6) & 0x03;
            }
        }
    }

    setPaletteForTile(x, y, paletteId) {
        const byteX = Math.floor(x/4), byteY = Math.floor(y/4);
        const theByte = this.currentImageMetaData[byteX + (byteY * 8)];
        const bitX = Math.floor(x / 2), bitY = Math.floor(y / 2);

        let newByte;
        if ((bitX % 2) === 0) {
            if ((bitY % 2) === 0) {
                // Top left, 0shift
                newByte = (theByte & 0xfc) + paletteId;
            } else {
                // Bottom left, 4shift
                newByte = (theByte & 0xcf) + (paletteId << 4);
            }
        } else {
            if ((bitY % 2) === 0) {
                // top right, 2shift
                newByte = (theByte & 0xf3) + (paletteId << 2);
            } else {
                // bottom right, 6shift
                newByte = (theByte & 0x3f) + (paletteId << 6);
            }
        }

        this.currentImageMetaData[byteX + (byteY * 8)] = newByte;
    }

    smartPreloadTiles() {
        // Preload commonly used tiles in all colors
        [
            // border
            0xe0, 0xe1, 0xf0, 0xf1, 0xe6, 0xf6,
            // Space
            0x80
        ].forEach(tileId => {
            [0, 1, 2, 3].forEach(palId => {
                this.preloadCache(tileId, this.state.palettes[palId]);
            });
        });

        // Preload built-in map tiles in their expected palette
        if (!GameData.currentGame) { return; }
        GameData.currentGame.tileData.forEach((tileObj, idx) => {
            let realTileId = idx << 1;
            if (idx > 7) {
                realTileId = 32 + ((idx - 8) << 1);
            }
            this.preloadCache(realTileId, this.state.palettes[tileObj.palette]);
            this.preloadCache(realTileId + 1, this.state.palettes[tileObj.palette]);
            this.preloadCache(realTileId + 16, this.state.palettes[tileObj.palette]);
            this.preloadCache(realTileId + 17, this.state.palettes[tileObj.palette]);

        });
    }

    getSelectedTileX() {
        return (this.state.currentTile % 16) * 16;
    }

    getSelectedTileY() {
        return Math.floor(this.state.currentTile / 16) * 16;
    }

    async generateImage() {
        if (!GameData.currentGame) { return; }

        await new Promise(resolve => this.setState({
            palettes: [
                GameData.currentGame.gamePaletteData.slice(0, 4),
                GameData.currentGame.gamePaletteData.slice(4, 8),
                GameData.currentGame.gamePaletteData.slice(8, 12),
                GameData.currentGame.gamePaletteData.slice(12, 16)                
            ]
        }, resolve));


        await Promise.all(this.currentImageTileData.map(async (tile, idx) => this.drawTile(tile, idx)));
        this.undoManager.reset();
    }
    
    setActiveTile(event) {
        const bounds = event.target.getBoundingClientRect(), x = event.clientX - bounds.left, y = event.clientY - bounds.top;
        if (x < 256 && y < 256) {
            let tileX = Math.floor(x / 16), tileY = Math.floor(y / 16);

            // Lock to 16px grid instead of 8
            if (this.state.is16pxMode) {
                tileX &= 0xfe;
                tileY &= 0xfe;
            }

            this.setState({
                currentTile: tileX + (tileY * 16)
            });
            this.preloadCache(tileX + (tileY * 16), this.state.palettes[this.state.currentPalette]);
        }
    }

    updatePxMode(v) {
        this.setState({
            is16pxMode: !!v,
            // Clamp to 16px grid - we just always do this since going to 8px it's a noop anyway
            currentTile: ((this.state.currentTile % 16) & 0xfe) + ((Math.floor(this.state.currentTile / 16) & 0xfe) * 16)
        });
    }

    testPositionProtected(x, y) {
        if (!this.props.protectedRegions) {
            return false;
        }
        for (let i = 0; i < this.props.protectedRegions.length; i++) {
            let region = this.props.protectedRegions[i];
            if (
                x >= region.x1 && x <= region.x2 &&
                y >= region.y1 && y <= region.y2
            ) { 
                return true; 
            }
        }
        return false;
    }

    async setPalette(palette) {
        await new Promise(resolve => this.setState({currentPalette: palette}, resolve));
        this.preloadCache(this.state.currentTile, this.state.palettes[this.state.currentPalette]);
    }

    async doTyping(event) {
        if (this.state.isTypingMode &&
            document.activeElement.tagName.toLowerCase() !== 'textarea' &&
            !(document.activeElement.tagName.toLowerCase() === 'label' && 
            document.activeElement.getAttribute('type') === 'text') && 
            !event.ctrlKey
        ) {
            let newX = this.state.typingX, newY = this.state.typingY;

            if (this.testPositionProtected(this.state.typingX, this.state.typingY)) {
                return;
            }

            if (event.key.length === 1 && event.key >= ' ' && event.key <= '~') {
                await this.drawMetaTile(this.state.typingX, this.state.typingY, event.key.charCodeAt(0) + 0x60);
                newX++;
                event.preventDefault();
            } else { 
                switch (event.key) {
                    case 'Backspace':
                    case 'ArrowLeft':
                        --newX;
                        event.preventDefault();
                        break;
                    case 'Enter':
                    case 'ArrowDown':
                        ++newY;
                        event.preventDefault();
                        break;
                    case 'ArrowRight':
                        ++newX;
                        event.preventDefault();
                        break;
                    case 'ArrowUp':
                        --newY;
                        event.preventDefault();
                        break;
                    default:
                        // We don't care.
                }    
            }
                

            if (newX > 31) { newX = 0; newY += 1 }
            if (newX < 0) { newX = 31; --newY; }
            if (newY < 0) { newY = 29; }
            if (newY > 29) { newY = 0; }

            this.setState({
                typingX: newX,
                typingY: newY
            });

        }
    }

    async drawTile(tileId, tileIndex, palette = null) {
        const x = (tileIndex % 32);
        const y = Math.floor(tileIndex / 32);
        if (palette === null) {
            palette = this.getPaletteForTile(x, y);
        }
        this.preloadCache(tileId, palette);
        this.drawTilePipeline.push({x, y, palette, tileId});
        this.kickDrawTilePipeline();
    }

    async redrawTileset(tileIndex, tileX, tileY) {
        // We have to redraw 4 tiles, not just one! We patch one tile, but must patch all palettes.
        await Promise.all([
            this.drawTile(this.currentImageTileData[tileIndex], tileIndex),
            this.drawTile(this.currentImageTileData[tileIndex+1], tileIndex+1),
            this.drawTile(this.currentImageTileData[tileIndex+32], tileIndex+32),
            this.drawTile(this.currentImageTileData[tileIndex+33], tileIndex+33)
        ]);
    }

    async drawMetaTile(tileX, tileY, thisTile, use16px = false, palette = null) {
        const tileIndex = (tileY & 0xfe)*32 + (tileX & 0xfe);
        if (this.editorLocked) { return; }
        this.editorLocked = true;
        if (palette === null) {
            palette = this.state.currentPalette;
        }

        const {isTypingMode, typingX, typingY} = this.state;
        
        if (use16px) {
            tileX &= 0xfe; tileY &= 0xfe;
            const oldTiles = [
                this.currentImageTileData[tileX + ((tileY) * 32)],
                this.currentImageTileData[tileX + 1 + ((tileY) * 32)],
                this.currentImageTileData[tileX + ((tileY + 1) * 32)],
                this.currentImageTileData[tileX + 1 + ((tileY + 1) * 32)]
            ];
            const oldPalette = this.getPaletteIdForTile(tileX, tileY);

            await this.undoManager.runUndoableStep(
                async () => {
                    this.currentImageTileData[tileX + ((tileY) * 32)] = thisTile;
                    this.setPaletteForTile(tileX, tileY, palette);
                    this.currentImageTileData[tileX + 1 + ((tileY) * 32)] = thisTile + 1;
                    this.setPaletteForTile(tileX + 1, tileY, palette);
                    this.currentImageTileData[tileX + ((tileY + 1) * 32)] = thisTile + 16;
                    this.setPaletteForTile(tileX, tileY + 1, palette);
                    this.currentImageTileData[tileX + 1 + ((tileY + 1) * 32)] = thisTile + 17;
                    this.setPaletteForTile(tileX + 1, tileY + 1, palette);
                    this.redrawTileset(tileIndex);
                }, 
                async () => {
                    this.currentImageTileData[tileX + ((tileY) * 32)] = oldTiles[0];
                    this.setPaletteForTile(tileX, tileY, oldPalette);
                    this.currentImageTileData[tileX + 1 + ((tileY) * 32)] = oldTiles[1];
                    this.setPaletteForTile(tileX + 1, tileY, oldPalette);
                    this.currentImageTileData[tileX + ((tileY + 1) * 32)] = oldTiles[2];
                    this.setPaletteForTile(tileX, tileY + 1, oldPalette);
                    this.currentImageTileData[tileX + 1 + ((tileY + 1) * 32)] = oldTiles[3];
                    this.setPaletteForTile(tileX + 1, tileY + 1, oldPalette);
                    this.redrawTileset(tileIndex);
                }, 
                `16px-${tileX}-${tileY}-${thisTile}-${palette}`
            )

        } else {
            const oldTileId = this.currentImageTileData[tileX + (tileY * 32)];
            const oldPalette = this.getPaletteIdForTile(tileX, tileY);
            await this.undoManager.runUndoableStep(
                async () => {
                    this.currentImageTileData[tileX + (tileY * 32)] = thisTile;
                    this.setPaletteForTile(tileX, tileY, palette);
                    this.redrawTileset(tileIndex);
                },
                async () => {
                    this.currentImageTileData[tileX + (tileY * 32)] = oldTileId;
                    this.setPaletteForTile(tileX, tileY, oldPalette);
                    this.redrawTileset(tileIndex);
                    if (isTypingMode) {
                        this.setState({typingX, typingY})
                    }
                },
                `16px-${tileX}-${tileY}-${thisTile}-${palette}`

            )
        }

        ApplicationState.setCurrentPageUnsaved();

        this.editorLocked = false;
    }

    async _preloadCache(tileId, palette) {
        const rawImg = await NesGraphics.convertToBase64Image(this.combinedTileData, palette, tileId);
        const tile = new Image();
        tile.src = rawImg;
        await new Promise((resolve, reject) => { tile.onload = resolve; tile.onerror = reject; });
        return tile;
    }

    preloadCache(tileId, palette) {
        let tileKey;

        if (this.state.is16pxMode) {
            let pal = palette.join('_') + '__';
            // We can safely assume that tileId is a top-left tile by editor restrictions, which simplifies this a bit
            if (!this.tileCache[pal + tileId]) { this.tileCache[pal + tileId] = this._preloadCache(tileId, palette); }
            if (!this.tileCache[pal + (tileId + 1)]) { this.tileCache[pal + (tileId + 1)] = this._preloadCache(tileId + 1, palette); }
            if (!this.tileCache[pal + (tileId + 16)]) { this.tileCache[pal + (tileId + 16)] = this._preloadCache(tileId + 16, palette); }
            if (!this.tileCache[pal + (tileId + 17)]) { this.tileCache[pal + (tileId + 17)] = this._preloadCache(tileId + 17, palette); }

        } else {
            tileKey = palette.join('_') + '__' + tileId;
            if (!this.tileCache[tileKey]) {
                this.tileCache[tileKey] = this._preloadCache(tileId, palette, tileKey);
            }
        }
    }

    async kickDrawTilePipeline() {
        if (this.drawTilePipelineRunning) {
            return;
        }
        this.drawTilePipelineRunning = true;
        while (this.drawTilePipeline.length) {
            const { x, y, palette, tileId } = this.drawTilePipeline.shift();
            const tileKey = palette.join('_') + '__' + tileId;

            this.canvasContext.drawImage(await this.tileCache[tileKey], x*16, y*16, 16, 16);
        }
        this.drawTilePipelineRunning = false;
    }

    touchMove(event) {
        this.doTileUpdate(event, true); 
    }

    async doTileUpdate(event, cancelEvent = false) {
        const bounds = event.target.getBoundingClientRect(), 
            x = (event.clientX || event.touches[0].clientX) - bounds.left, 
            y = (event.clientY || event.touches[0].clientY) - bounds.top;
        if (!this.state.isDragging) {
            return;
        }
        if (x < 512 && y < (512 - 16)) {
            if (cancelEvent) { event.preventDefault(); }
            let tileX = Math.floor(x / 16), tileY = Math.floor(y / 16);

            if (event.buttons === 0x02) {
                // Rightclick - copy the tile instead.
                if (this.state.is16pxMode) {
                    tileX &= 0xfe;
                    tileY &= 0xfe;
                }
                const pal = this.getPaletteIdForTile(tileX, tileY);
                this.setState({
                    currentTile: this.currentImageTileData[tileX + (tileY * 32)]
                });
                // Frick it, raw javascript to set palette ui
                document.querySelector(`#toggle-palette-${pal}`).click();
                return;
            }

            if (this.testPositionProtected(tileX, tileY)) {
                return;
            }
            if (this.state.is16pxMode && (
                this.testPositionProtected(tileX + 1, tileY) || this.testPositionProtected(tileX, tileY + 1) ||
                this.testPositionProtected(tileX + 1, tileY + 1)
            )) {
                return;
            }

            if (this.state.isTypingMode) {
                this.setState({
                    typingX: tileX,
                    typingY: tileY
                });
                return;
            }

            if (this.state.is16pxMode) {
                if (
                    (this.currentImageTileData[tileX + ((tileY) * 32)] !== this.state.currentTile || this.getPaletteIdForTile(tileX, tileY) !== this.state.currentPalette) ||
                    (this.currentImageTileData[tileX + 1 + ((tileY) * 32)] !== this.state.currentTile || this.getPaletteIdForTile(tileX + 1, tileY) !== this.state.currentPalette) ||
                    (this.currentImageTileData[tileX + ((tileY + 1) * 32)] !== this.state.currentTile || this.getPaletteIdForTile(tileX, tileY + 1) !== this.state.currentPalette) ||
                    (this.currentImageTileData[tileX + 1 + ((tileY + 1) * 32)] !== this.state.currentTile || this.getPaletteIdForTile(tileX + 1, tileY + 1) !== this.state.currentPalette)
                    
                ) {
                    this.drawMetaTile(tileX, tileY, this.state.currentTile, true);
                }

            } else {
                if (this.currentImageTileData[tileX + (tileY * 32)] !== this.state.currentTile || this.getPaletteIdForTile(tileX, tileY) !== this.state.currentPalette) {
                    this.drawMetaTile(tileX, tileY, this.state.currentTile);
                }
            }

        }
    }

    async saveData() {
        if (this.props.fieldName) {
            GameData.replaceCurrentGame({
                ...GameData.currentGame,
                [this.props.fieldName]: [...this.currentImageTileData, ...this.currentImageMetaData]
            });
        } else if (this.props.screenId) {
            const staticScreens = JSON.parse(JSON.stringify(GameData.currentGame.staticScreens));
            const sIndex = staticScreens.findIndex(a => a.id === this.props.screenId);
            staticScreens[sIndex].data = [...this.currentImageTileData, ...this.currentImageMetaData];
            GameData.replaceCurrentGame({
                ...GameData.currentGame,
                staticScreens
            });
        }

        if (this.props.onSave) {
            await this.props.onSave();
        }
        ApplicationState.unsetCurrentPageUnsaved();
        ApplicationState.addAlert('success', 'Screen saved successfully!');
    }

    showConfirm(action) {
        this.confirmData.promise = new Promise((resolve, reject) => {
            this.confirmData.resolve = resolve;
            this.confirmData.reject = reject;
        });
        this.setState({
            awaitingConfirm: true,
            awaitingConfirmAction: action
        });

        return this.confirmData.promise;
    }

    cancelConfirm() {
        this.confirmData.reject();
        this.confirmData = {
            promise: null,
            resolve: null, 
            reject: null
        };
        this.setState({awaitingConfirm: false})
    }

    continueConfirm() {
        this.confirmData.resolve();
        this.confirmData = {
            promise: null,
            resolve: null, 
            reject: null
        };
        this.setState({awaitingConfirm: false})
    }

    async actionClearToTile() {
        try {
            await this.showConfirm('Clear nametable to selected tile');
        } catch (e) { 
            // User action was cancelled, don't do it!
            return;
        }

        // Reset the data, redraw everything.
        let palByte = this.state.currentPalette + (this.state.currentPalette << 2);
        palByte += palByte << 4;
        
        for (let i = 0; i < this.currentImageMetaData.length; i++) {
           this.currentImageMetaData[i] = palByte;
        }
        for (let i = 0; i < this.currentImageTileData.length; i++) {
            this.currentImageTileData[i] = this.state.currentTile;
            this.drawTile(this.state.currentTile, i);
        }
        this.undoManager.reset();
    }

    async actionDrawBorder() {
        try {
            await this.showConfirm('Draw border over existing screen');
        } catch (e) { 
            // User action was cancelled, don't do it!
            return;
        }

        const xBorder = 2, yBorder = 4;
        let borderTile = null;

        for (let y = 0; y < this.state.height; y++) {
            for (let x = 0; x < this.state.width; x++) {
                // Corners
                if (x === xBorder && y === yBorder) { borderTile = 0xe0; }
                else if (x === xBorder && y === this.state.height - yBorder - 1) { borderTile = 0xf0; }
                else if (x === this.state.width - 1 - xBorder && y === yBorder) { borderTile = 0xe1; }
                else if (x === this.state.width - 1 - xBorder && y === this.state.height - 1 - yBorder) { borderTile = 0xf1; }
                // Top & bottom
                else if (x > xBorder && x < this.state.width - xBorder - 1 && (y === yBorder || y === this.state.height - yBorder - 1)) { borderTile = 0xf6; }
                // Left & right
                else if ((x === xBorder || x === this.state.width - xBorder - 1) && y > yBorder && y < this.state.width - yBorder - 2) { borderTile = 0xe6; }
                else {
                    borderTile = null;
                }
                if (borderTile) {
                    await this.drawMetaTile(x, y, borderTile);
                }
            }
        }
        this.undoManager.reset();
    }

    
    render() {
        // HACK: Hide everything when the emulator is visible, so performance isn't ruined.
        if (ApplicationState.emulatorIsVisible) { 
            return <div><em>Emulator running</em></div>
        }
        return (
            <div className={"nam-editor" + (this.state.isReadOnly ? ' read-only' : '')} tabIndex={-1} onKeyDown={e => this.undoManager.onKeyDown(e)}>
                <Modal show={this.state.awaitingConfirm} onHide={() => this.cancelConfirm()}>
                    <Modal.Header closeButton>
                        <Modal.Title>Are you sure?</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to do this? You will not be able to undo!
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => this.cancelConfirm()}>Cancel</Button>
                        <Button variant="danger" onClick={() => this.continueConfirm()}>{this.state.awaitingConfirmAction}</Button>
                    </Modal.Footer>
                </Modal>
                <Container>
                    <Row>
                        <Col>
                            <div className="canvas-holder">
                                <canvas 
                                    className="editor-nametable" width="512" height="480" 
                                    onMouseDown={event => { this.setState({isDragging: true}, () => this.doTileUpdate(event)); }} 
                                    onTouchStart={event => { this.setState({isDragging: true}, () => this.doTileUpdate(event)); }} 
                                    onMouseMove={event => this.doTileUpdate(event) }
                                    onMouseUp={() => this.clearDragging()} 
                                    onTouchEnd={() => this.clearDragging()}
                                    draggable={false} 
                                    onContextMenu={event => event.preventDefault()} 
                                    ref={this.canvas} 
                                />
                                <div className="text-indicator" style={{display: this.state.isTypingMode ? 'block': 'none', top: this.state.typingY*16, left: this.state.typingX*16}} />
                                {this.state.isReadOnly ? <div className="overlay"></div> : <></>}
                            </div>
                        </Col>
                        <Col className="editor-stuff">
                            <h6 className="tile-selection">
                                <span>Tile selection</span>
                                <ToggleButtonGroup size="sm" defaultValue={false} name="sizeOption" onChange={v => this.updatePxMode(v)}>
                                    <ToggleButton variant="outline-primary" size="sm" id="toggle-size-8" value={false}>8px</ToggleButton>
                                    <ToggleButton variant="outline-primary" size="sm" id="toggle-size-16" value={true}>16px</ToggleButton>
                                </ToggleButtonGroup>
                            </h6>
                            <div className="chr-holder" onClick={event => this.setActiveTile(event)} onContextMenu={event => event.preventDefault()} >
                                <ChrFile data={this.combinedTileData} palette={this.state.palettes[this.state.currentPalette]} />
                                <div className={"chr-indicator " + (this.state.is16pxMode ? 't16px' : 't8px')} style={{left: this.getSelectedTileX(), top: this.getSelectedTileY()}} />
                            </div>
                            <h6 className="mt-3">Tile Palette</h6>
                            <div className="toggle-palette">
                                <ToggleButtonGroup className="palette-selection" defaultValue={0} name="paletteOption" onChange={(v) => this.setPalette(v)} ref={this.paletteGroup}>
                                    {this.state.palettes.map((pal, idx) => (
                                        <ToggleButton variant="outline-primary" key={'pal-' + idx} id={'toggle-palette-' + idx} value={idx}>
                                            <PalettePreview palette={pal} />
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </div>
                            {/* No h3 here, this one seems obvious */}
                            <div className="toggle-mode mt-3">
                                <ToggleButtonGroup className="typing-selection" defaultValue={false} name="typingOption" onChange={v => this.setState({isTypingMode: !!v})}>
                                    <ToggleButton variant="outline-primary" id={'toggle-typing-off'} value={false}>Drawing Mode</ToggleButton>
                                    <ToggleButton variant="outline-primary" id="toggle-typing-on" value={true}>Typing Mode</ToggleButton>
                                </ToggleButtonGroup>
                            </div>
                            <div className="toggle-mode mt-3">
                                <DropdownButton variant="outline-primary" title="Actions">
                                    <Dropdown.Item onClick={() => this.actionClearToTile()}>Clear screen with current tile</Dropdown.Item>
                                    <Dropdown.Item onClick={() =>  this.actionDrawBorder()}>Draw border</Dropdown.Item>
                                </DropdownButton>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="button-footer-bar">
                            <Button variant="primary" onClick={() => this.saveData() }>Save Changes</Button>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }
}